import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Edit3,
  Loader2,
  Plus,
  Save,
  Tags,
  Trash2,
  X
} from 'lucide-react';
import api from '../../utils/api';

function normalizeKeywordInputs(items) {
  return Array.from(
    new Set(
      (Array.isArray(items) ? items : [])
        .map((item) => String(item || '').trim())
        .filter(Boolean)
    )
  );
}

export default function UserLabelRulesTab() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [rules, setRules] = useState([]);
  const [editingRule, setEditingRule] = useState(null);
  const [labelNameInput, setLabelNameInput] = useState('');
  const [keywordInputs, setKeywordInputs] = useState(['']);
  const [editEnabled, setEditEnabled] = useState(true);

  const activeRuleCount = useMemo(
    () => rules.filter((item) => item.is_enabled).length,
    [rules]
  );

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.get('/admin/user-label-rules');
      if (response.data.success) {
        const data = response.data.data || response.data || {};
        setRules(Array.isArray(data.list) ? data.list : []);
      }
    } catch (err) {
      console.error('获取用户标签规则失败:', err);
      setError(err?.response?.data?.error || '获取用户标签规则失败');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditor = (rule) => {
    const normalizedKeywords = Array.isArray(rule?.keywords) && rule.keywords.length > 0
      ? rule.keywords
      : [''];

    setEditingRule(rule);
    setLabelNameInput(rule?.label_name || '');
    setKeywordInputs(normalizedKeywords);
    setEditEnabled(Boolean(rule?.is_enabled));
    setError('');
    setSuccessMsg('');
  };

  const handleOpenCreate = () => {
    setEditingRule({ id: null, label_name: '', is_enabled: true, isNew: true });
    setLabelNameInput('');
    setKeywordInputs(['']);
    setEditEnabled(true);
    setError('');
    setSuccessMsg('');
  };

  const handleCloseEditor = () => {
    setEditingRule(null);
    setLabelNameInput('');
    setKeywordInputs(['']);
    setEditEnabled(true);
  };

  const handleKeywordChange = (index, value) => {
    setKeywordInputs((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleAddKeyword = () => {
    setKeywordInputs((prev) => [...prev, '']);
  };

  const handleRemoveKeyword = (index) => {
    setKeywordInputs((prev) => {
      const next = prev.filter((_, itemIndex) => itemIndex !== index);
      return next.length > 0 ? next : [''];
    });
  };

  const handleSaveRule = async () => {
    if (!editingRule) return;

    const isCreating = Boolean(editingRule?.isNew);
    const trimmedLabelName = String(labelNameInput || '').trim();
    const keywords = normalizeKeywordInputs(keywordInputs);
    setSaving(true);
    setError('');
    setSuccessMsg('');

    try {
      const response = isCreating
        ? await api.post('/admin/user-label-rules', {
          labelName: trimmedLabelName,
          keywords,
          isEnabled: editEnabled
        })
        : await api.put(`/admin/user-label-rules/${editingRule.id}`, {
          keywords,
          isEnabled: editEnabled
        });

      if (response.data.success) {
        setSuccessMsg(isCreating ? `${trimmedLabelName} 标签创建成功` : `${editingRule.label_name} 规则保存成功`);
        handleCloseEditor();
        await fetchRules();
      }
    } catch (err) {
      console.error('保存用户标签规则失败:', err);
      setError(err?.response?.data?.error || '保存用户标签规则失败');
    } finally {
      setSaving(false);
    }
  };

  const renderKeywordPreview = (keywords) => {
    const normalized = Array.isArray(keywords) ? keywords.filter(Boolean) : [];
    if (normalized.length <= 0) {
      return <span className="text-sm text-gray-400">暂无命中词</span>;
    }

    return (
      <div className="flex flex-wrap gap-2">
        {normalized.slice(0, 8).map((keyword) => (
          <span
            key={keyword}
            className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200"
          >
            {keyword}
          </span>
        ))}
        {normalized.length > 8 ? (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
            +{normalized.length - 8}
          </span>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {error ? (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      ) : null}

      {successMsg ? (
        <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <CheckCircle2 className="h-4 w-4" />
          <span>{successMsg}</span>
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-gray-200 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Tags className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">用户标签规则</h2>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              默认展示全部标签，可按标签逐个维护命中词，并控制该标签是否参与自动打标。
            </p>
          </div>

          <div className="flex flex-wrap gap-3 text-sm">
            <div className="rounded-xl bg-gray-50 px-4 py-2 text-gray-600">
              标签总数：<span className="font-semibold text-gray-900">{rules.length}</span>
            </div>
            <div className="rounded-xl bg-emerald-50 px-4 py-2 text-emerald-700">
              已启用：<span className="font-semibold">{activeRuleCount}</span>
            </div>
            <button
              type="button"
              onClick={handleOpenCreate}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              新增标签
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center px-6 py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 p-6 xl:grid-cols-2">
            {rules.map((rule) => (
              <div
                key={rule.id}
                className="rounded-2xl border border-gray-200 bg-gray-50 p-5"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-gray-900">{rule.label_name}</h3>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                          rule.is_enabled
                            ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200'
                            : 'bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200'
                        }`}
                      >
                        {rule.is_enabled ? '已启用' : '已停用'}
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                      命中词 {rule.keyword_count || 0} 个
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleOpenEditor(rule)}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
                  >
                    <Edit3 className="h-4 w-4" />
                    修改
                  </button>
                </div>

                <div className="mt-4">
                  {renderKeywordPreview(rule.keywords)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editingRule ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{editingRule?.isNew ? '新增标签' : '编辑标签规则'}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {editingRule?.isNew ? '创建后标签名不可修改，这里可同时设置启停状态和命中词。' : '标签名创建后不可修改，这里只维护启停状态和命中词。'}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseEditor}
                disabled={saving}
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6 overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">标签名</label>
                  <input
                    type="text"
                    value={labelNameInput}
                    onChange={(e) => setLabelNameInput(e.target.value)}
                    disabled={!editingRule?.isNew}
                    placeholder="请输入标签名"
                    className={`w-full rounded-lg border px-4 py-2.5 text-sm outline-none ${
                      editingRule?.isNew
                        ? 'border-gray-300 bg-white text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                        : 'border-gray-200 bg-gray-100 text-gray-500'
                    }`}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">标签状态</label>
                  <button
                    type="button"
                    onClick={() => setEditEnabled((prev) => !prev)}
                    className={`inline-flex w-full items-center justify-between rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
                      editEnabled
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-gray-200 bg-gray-50 text-gray-600'
                    }`}
                  >
                    <span>{editEnabled ? '启用中' : '已停用'}</span>
                    <span>{editEnabled ? '点击停用' : '点击启用'}</span>
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">命中词</h4>
                    <p className="mt-1 text-xs text-gray-500">仅做“包含匹配”，可新增、删除、修改。</p>
                  </div>

                  <button
                    type="button"
                    onClick={handleAddKeyword}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <Plus className="h-4 w-4" />
                    新增命中词
                  </button>
                </div>

                <div className="space-y-3">
                  {keywordInputs.map((keyword, index) => (
                    <div key={`${editingRule.id}-${index}`} className="flex items-center gap-3">
                      <input
                        type="text"
                        value={keyword}
                        onChange={(e) => handleKeywordChange(index, e.target.value)}
                        placeholder={`请输入第 ${index + 1} 个命中词`}
                        className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveKeyword(index)}
                        className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 p-2.5 text-red-600 hover:bg-red-100"
                        title="删除命中词"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                type="button"
                onClick={handleCloseEditor}
                disabled={saving}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleSaveRule}
                disabled={saving || (editingRule?.isNew && !labelNameInput.trim())}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {editingRule?.isNew ? '创建标签' : '保存规则'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
